import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isLoggedIn } from '../utils/tokenUtils';
import { getSettings } from '../api/settingsApi';
import { getProducts } from '../api/productApi';
import { getActiveBanners } from '../api/bannerApi';
import styles from './MainPage.module.css';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function MainPage() {
    const [loggedIn, setLoggedIn] = useState(false);
    const [settings, setSettings] = useState({});
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [newProducts, setNewProducts] = useState([]);
    const [banners, setBanners] = useState([]);
    const [currentBanner, setCurrentBanner] = useState(0);
    const bannerTimer = useRef(null);
    const navigate = useNavigate();
    const [bestProducts, setBestProducts] = useState([]);

    useEffect(() => {
        setLoggedIn(isLoggedIn());
        loadData();
    }, []);

    useEffect(() => {
        if (banners.length <= 1) return;
        bannerTimer.current = setInterval(() => {
            setCurrentBanner(prev => (prev + 1) % banners.length);
        }, 4000);
        return () => clearInterval(bannerTimer.current);
    }, [banners]);

    const loadData = async () => {
        try {
            const [settingsRes, productsRes, bannersRes] = await Promise.all([
                getSettings(),
                getProducts(),
                getActiveBanners()
            ]);
            const s = settingsRes.data;
            setSettings(s);
            setBanners(bannersRes.data || []);

            const allProducts = productsRes.data.products || productsRes.data.content || productsRes.data;
            const featuredIds = (s.main_featured_products || '').split(',').filter(Boolean).map(Number);
            if (featuredIds.length > 0) {
                const featured = featuredIds.map(id => allProducts.find(p => p.productId === id)).filter(Boolean);
                setFeaturedProducts(featured);
            }
            setNewProducts(allProducts.slice(0, 4));

            
            const bestRes = await getProducts({ sort: 'popular', size: 4 });
            setBestProducts(bestRes.data.products || []);

        } catch (err) {
            console.error('메인 페이지 로딩 실패', err);
        }
    };

    const handleBannerClick = (banner) => {
        if (banner.linkType === 'EVENT') navigate(`/events/${banner.linkValue}`);
        else if (banner.linkType === 'PRODUCT') navigate(`/products/${banner.linkValue}`);
        else if (banner.linkType === 'URL') window.open(banner.linkValue, '_blank');
    };

    const goToBanner = (index) => {
        clearInterval(bannerTimer.current);
        setCurrentBanner(index);
        bannerTimer.current = setInterval(() => {
            setCurrentBanner(prev => (prev + 1) % banners.length);
        }, 4000);
    };

    return (
        <div className={styles.container}>

            {/* 메인 배너 슬라이더 */}
            {banners.length > 0 ? (
                <section className={styles.bannerSlider}>
                    <div className={styles.bannerTrack}
                        style={{ transform: `translateX(-${currentBanner * 100}%)` }}>
                        {banners.map((banner, i) => (
                            <div key={banner.bannerId} className={styles.bannerSlide}
                                onClick={() => handleBannerClick(banner)}
                                style={{ cursor: banner.linkType !== 'NONE' ? 'pointer' : 'default' }}>
                                <img src={`${API_URL}${banner.imageUrl}`}
                                    alt={banner.title} className={styles.bannerImage} />
                                {banner.title && (
                                    <div className={styles.bannerOverlay}>
                                        <p className={styles.bannerTitle}>{banner.title}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    {banners.length > 1 && (
                        <>
                            <button className={`${styles.bannerArrow} ${styles.bannerArrowLeft}`}
                                onClick={() => goToBanner((currentBanner - 1 + banners.length) % banners.length)}>
                                ‹
                            </button>
                            <button className={`${styles.bannerArrow} ${styles.bannerArrowRight}`}
                                onClick={() => goToBanner((currentBanner + 1) % banners.length)}>
                                ›
                            </button>
                            <div className={styles.bannerDots}>
                                {banners.map((_, i) => (
                                    <span key={i}
                                        className={`${styles.bannerDot} ${i === currentBanner ? styles.bannerDotActive : ''}`}
                                        onClick={() => goToBanner(i)} />
                                ))}
                            </div>
                            <div className={styles.bannerCounter}>
                                {currentBanner + 1} / {banners.length}
                            </div>
                        </>
                    )}
                </section>
            ) : (
                /* 배너 없을 때 기본 히어로 */
                <section className={styles.hero}>
                    <div className={styles.heroContent}>
                        <p className={styles.heroSub}>NEW COLLECTION 2026</p>
                        <h1 className={styles.heroTitle}>DISCOVER<br />YOUR STYLE</h1>
                        <Link to="/products" className={styles.heroButton}>SHOP NOW</Link>
                    </div>
                </section>
            )}

            {/* 추천 상품 */}
            {featuredProducts.length > 0 && (
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>FEATURED</h2>
                    <div className={styles.productGrid}>
                        {featuredProducts.map(product => (
                            <div key={product.productId} className={styles.productCard}
                                onClick={() => navigate(`/products/${product.productId}`)}>
                                <div className={styles.productImageWrap}>
                                    <img src={`${API_URL}${product.imageUrl}`}
                                        alt={product.name} className={styles.productImage} />
                                    {product.discountRate > 0 && (
                                        <span className={styles.discountBadge}>{product.discountRate}%</span>
                                    )}
                                </div>
                                <div className={styles.productInfo}>
                                    <p className={styles.productName}>{product.name}</p>
                                    <p className={styles.productPrice}>{product.price?.toLocaleString()}원</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* 신상품 */}
            {/* 베스트 상품 */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>BEST</h2>
                <div className={styles.productGrid}>
                    {bestProducts.map((product, i) => (
                        <div key={product.productId} className={styles.productCard}
                            onClick={() => navigate(`/products/${product.productId}`)}>
                            <div className={styles.productImageWrap}>
                                <span className={styles.rankBadge}>{i + 1}</span>
                                <img src={`${API_URL}${product.imageUrl}`}
                                    alt={product.name} className={styles.productImage} />
                                {product.discountRate > 0 && (
                                    <span className={styles.discountBadge}>{product.discountRate}%</span>
                                )}
                            </div>
                            <div className={styles.productInfo}>
                                <p className={styles.productName}>{product.name}</p>
                                <p className={styles.productPrice}>{product.price?.toLocaleString()}원</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>NEW ARRIVALS</h2>
                <div className={styles.productGrid}>
                    {newProducts.map(product => (
                        <div key={product.productId} className={styles.productCard}
                            onClick={() => navigate(`/products/${product.productId}`)}>
                            <div className={styles.productImageWrap}>
                                <img src={`${API_URL}${product.imageUrl}`}
                                    alt={product.name} className={styles.productImage} />
                                {product.discountRate > 0 && (
                                    <span className={styles.discountBadge}>{product.discountRate}%</span>
                                )}
                            </div>
                            <div className={styles.productInfo}>
                                <p className={styles.productName}>{product.name}</p>
                                <p className={styles.productPrice}>{product.price?.toLocaleString()}원</p>
                            </div>
                        </div>
                    ))}
                </div>
                <div className={styles.moreBtn}>
                    <Link to="/products" className={styles.moreBtnLink}>전체 상품 보기</Link>
                </div>
            </section>

            {/* 멤버십 배너 */}
            {!loggedIn && (
                <section className={styles.memberBanner}>
                    <p className={styles.memberTitle}>MEMBERSHIP</p>
                    <p className={styles.memberSub}>회원가입하고 다양한 혜택을 받아보세요</p>
                    <Link to="/register" className={styles.memberButton}>JOIN US</Link>
                </section>
            )}
        </div>
    );
}

export default MainPage;